import config from '@packages/config';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CheckboxWithLabel,
  Grid,
  Notification,
  ToggleButtonGroup,
  Select,
  TextField,
  Typography,
  useDeviceType,
} from '@packages/shared';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { DateField } from '@packages/shared/src/components/DateField/DateField';
import { useCustomer } from '@packages/utilities';
import type { CaptchaData } from '@packages/captcha';
import { ActivityOverlay } from '../ActivityOverlay';
import { createFormSubSelectionsSelect } from '../helpers/createFormElements';
import { basicContainer, styleCardHeader, styleContentTop, stylePanel } from '../styles';
import type {
  CheckboxID,
  ErrorType,
  FormRequiredFields,
  FormValues,
  InputID,
  FormSubmitResult,
} from '../types';
import { CheckboxIDs, InputIDs } from '../types';
import { prefillForm } from '../helpers/customerValue';
import { formMessages, salutationSelectMessages } from '../messages';
import type { SuccessPageProps } from '../SuccessPage';
import { SuccessPage } from '../SuccessPage';
import { FormFooterArea } from '../FormFooterArea';
import { formatErrorMsg } from '../helpers/formatErrorMsg';
import type { SchemaType } from '../helpers/validationSchema';
import type { PostDataMessage } from './helpers/handleSubmit';
import { handleSubmit } from './helpers/handleSubmit';
import { CaptchaDialog } from '../CaptchaDialog';

export enum GeneralFormTestIds {
  main = 'contactform-container',
  header = 'contactform-header',
  form = 'contactform-form',
}

export type GeneralFormProps = {
  /** Object containing the initial Form values */
  initialValues: FormValues;
  /** Yup Validation Schema */
  validationSchema: SchemaType;
  /** Array of required Fields */
  requiredFields: FormRequiredFields;
  /** Fields that you want to prefill with user's data (if logged in) */
  fieldsToPrefill: InputIDs[];
  /** Function to convert the form's data into data to post */
  convertFormDataToPostData: (formValues: FormValues) => PostDataMessage;
  successPage: SuccessPageProps;
  /** Texts for the submit button */
  submitButton: { enabled: string; disabled: string };
  /** Headline and description above the form. Will not be rendered if ommitted */
  headingArea?: { headline: string; description: string };
  /** Card Header directly above the form. Will not be rendered if ommitted  */
  formHeading?: string;
  /** Enter custom placeholder and label for the message field  */
  customMessageField?: { placeholder: string; label: string };
  /** Render the subject selection */
  showSubject?: boolean;
  /** Render checkboxes */
  showOptions?: boolean;
  /** Render a checkbox above the submit button */
  showTerms?: boolean;
  /** Render the salutation selection */
  showSalutation?: boolean;
  /** Render the birthday input instead of the phone input */
  showBirthdate?: boolean;
};

export const GeneralForm = ({
  initialValues,
  validationSchema,
  fieldsToPrefill,
  convertFormDataToPostData,
  requiredFields,
  formHeading,
  successPage,
  headingArea,
  customMessageField,
  showSubject,
  showOptions,
  submitButton,
  showTerms,
  showSalutation,
  showBirthdate,
}: GeneralFormProps) => {
  const intl = useIntl();
  const { customer } = useCustomer();
  const { isMobile } = useDeviceType();

  const [errorNotification, setErrorNotification] = useState({ show: false, message: '' });
  const [formSubmitResult, setFormSubmitResult] = useState<FormSubmitResult | null>(null);
  const [usedCaptchaData, setUsedCaptchaData] = useState<CaptchaData | undefined>(undefined);
  const [formData, setFormData] = useState<FormValues | undefined>(undefined);
  const handleCaptcha = (captchaData: CaptchaData) => {
    setUsedCaptchaData(captchaData);
  };
  const onSubmit = (formValues: FormValues) => {
    setFormData(formValues);
    return handleSubmit(
      setFormSubmitResult,
      (errMsg) => {
        setErrorNotification({ show: true, message: errMsg });
      },
      intl,
      convertFormDataToPostData(formValues),
      'message',
      isMobile ? 'mob' : 'web',
      handleCaptcha,
    );
  };
  const captchaSuccess = () => {
    if (formData) {
      onSubmit(formData).then(() => {});
    }
  };
  // state of the whole form
  const {
    values,
    errors,
    setFieldValue,
    setFieldTouched,
    isValid,
    getFieldProps,
    touched,
    dirty,
    isSubmitting,
    submitForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  // the form is valid when all fields are valid and the form has been changed (i. e. it is "dirty")
  const formIsValid = isValid && dirty;

  // If we are logged in as a customer, we prefill the formfields with the existing data
  useEffect(() => {
    prefillForm(fieldsToPrefill, customer, values, (value, id) => {
      setFieldValue(id, value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const basicPropsFor = (inputId: InputID) => {
    const fieldProps = getFieldProps(inputId);
    // removing empty value when birthday because DateField->DatePicker can not handle empty strings for validate dates
    if (fieldProps.value === '' && inputId === InputIDs.BIRTHDAY) {
      fieldProps.value = undefined;
    }
    return {
      ...fieldProps,
      id: inputId,
      placeholder:
        formMessages[inputId].placeholder.defaultMessage !== ''
          ? intl.formatMessage(formMessages[inputId].placeholder)
          : undefined,
      onBlur: () => setFieldTouched(inputId, true),
      helperText:
        touched[inputId] && errors[inputId]
          ? formatErrorMsg(errors[inputId] as ErrorType, inputId, intl)
          : '',
      fullWidth: true,
      required: requiredFields[inputId],
      label: intl.formatMessage(formMessages[inputId]?.label),
      error: !!errors[inputId] && !!touched[inputId],
    };
  };

  /**
   * Gather the basic props (that only depend on the id) for each checkbox
   */
  const basicPropsForCheckbox = (checkboxId: CheckboxID) => ({
    checkboxProps: {
      ...getFieldProps(checkboxId),
      id: checkboxId,
      required: requiredFields[checkboxId],
    },
    label: intl.formatMessage(formMessages[checkboxId]?.label),
    sx: {
      marginY: 1,
      ...(errors[checkboxId] &&
        touched[checkboxId] && /* istanbul ignore next  */ { color: 'error.main' }),
    },
  });

  // show Success Page if form has been successfully submitted
  if (formSubmitResult) {
    return <SuccessPage {...successPage} />;
  }

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Notification
        open={errorNotification.show}
        severity="error"
        autoHideDuration={3500}
        onClose={() => setErrorNotification({ ...errorNotification, show: false })}
      >
        {errorNotification.message}
      </Notification>
      {usedCaptchaData !== undefined && (
        <CaptchaDialog captchaData={usedCaptchaData} captchaSuccess={captchaSuccess} />
      )}
      {headingArea && (
        <Box sx={styleContentTop} data-testid={GeneralFormTestIds.header}>
          <Typography variant="h3" component="div" sx={{ marginBottom: 2.25 }}>
            {headingArea.headline}
          </Typography>
          {headingArea.description}
        </Box>
      )}

      <Box sx={stylePanel} data-testid={GeneralFormTestIds.main}>
        <Card sx={basicContainer}>
          {formHeading && <CardHeader title={formHeading} sx={styleCardHeader} />}

          <CardContent sx={{ padding: 4 }}>
            <form
              onSubmit={(event) => event.preventDefault()}
              data-testid={GeneralFormTestIds.form}
            >
              <Grid
                container
                columns={[1, 1, 2]}
                sx={{ marginBottom: 3 }}
                columnSpacing={4}
                rowSpacing={3}
              >
                <Grid item xs={1} md={1}>
                  <TextField {...basicPropsFor(InputIDs.CUSTOMERID)} />
                </Grid>

                {showSalutation ? (
                  <Grid item xs={1} md={1}>
                    <ToggleButtonGroup
                      id={InputIDs.SALUTATION}
                      label={intl.formatMessage(formMessages[InputIDs.SALUTATION].label)}
                      options={(['notSpecified', 'female', 'male'] as const).map((value) => ({
                        value,
                        label: intl.formatMessage(salutationSelectMessages[value]),
                      }))}
                      value={`${values[InputIDs.SALUTATION]}`}
                      handleChange={(_e, value) => setFieldValue(InputIDs.SALUTATION, value)}
                    />
                  </Grid>
                ) : (
                  /* This is an empty grid item to make sure that on dektop and tablet
                  customerID is alone in its row if salutation is not rendered */
                  !isMobile && <Grid item xs={1} md={1} />
                )}

                <Grid item xs={1}>
                  <TextField {...basicPropsFor(InputIDs.FIRSTNAME)} />
                </Grid>

                <Grid item xs={1}>
                  <TextField {...basicPropsFor(InputIDs.LASTNAME)} />
                </Grid>

                <Grid item xs={1}>
                  <Grid container columnSpacing={1.5} rowSpacing={1.5}>
                    <Grid item xs>
                      <TextField {...basicPropsFor(InputIDs.STREET)} />
                    </Grid>
                    <Grid item sx={{ width: '7rem' }}>
                      <TextField {...basicPropsFor(InputIDs.HOUSENUMBER)} />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={1}>
                  <Grid container columnSpacing={1.5} rowSpacing={1.5}>
                    <Grid item sx={{ width: '9rem' }}>
                      <TextField {...basicPropsFor(InputIDs.POSTALCODE)} type="tel" />
                    </Grid>
                    <Grid item xs>
                      <TextField {...basicPropsFor(InputIDs.CITY)} />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={1}>
                  <TextField
                    {...basicPropsFor(InputIDs.EMAIL)}
                    placeholder={intl.formatMessage(formMessages[InputIDs.EMAIL].placeholder)}
                  />
                </Grid>

                <Grid item xs={1} data-testid={showBirthdate && InputIDs.BIRTHDAY}>
                  {showBirthdate ? (
                    <DateField
                      {...basicPropsFor(InputIDs.BIRTHDAY)}
                      onChange={(value) => setFieldValue(InputIDs.BIRTHDAY, value)}
                    />
                  ) : (
                    <TextField {...basicPropsFor(InputIDs.PHONENUMBER)} />
                  )}
                </Grid>

                {showSubject && (
                  <Grid item xs={1}>
                    <Select
                      {...basicPropsFor(InputIDs.SUBJECT)}
                      placeholder={intl.formatMessage(formMessages[InputIDs.SUBJECT].placeholder)}
                      items={createFormSubSelectionsSelect(
                        config.forms.contact.selectOptionsSubject,
                        intl,
                      )}
                      controlProps={{ fullWidth: true }}
                    />
                  </Grid>
                )}

                {showOptions && (
                  <Grid item xs={2}>
                    <Box sx={{ paddingLeft: 1, '& span': { typography: 'body2' } }}>
                      <CheckboxWithLabel
                        {...basicPropsForCheckbox(CheckboxIDs.CANCELADVERTISING)}
                      />
                      <CheckboxWithLabel {...basicPropsForCheckbox(CheckboxIDs.CANCELNL)} />
                      <CheckboxWithLabel {...basicPropsForCheckbox(CheckboxIDs.CANCELMARKETING)} />
                    </Box>
                  </Grid>
                )}
              </Grid>

              <TextField
                {...basicPropsFor(InputIDs.MESSAGE)}
                {...(customMessageField && {
                  placeholder: customMessageField.placeholder,
                  label: customMessageField.label,
                })}
                type="textarea"
                multiline
                minRows={4}
                maxRows={8}
              />

              {showTerms && (
                <Box sx={{ paddingLeft: 1, marginY: 1, '& span': { typography: 'body2' } }}>
                  <CheckboxWithLabel
                    {...basicPropsForCheckbox(CheckboxIDs.TERMS)}
                    label={
                      <span
                        // there might be html inside the message that needs to be rendered
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: intl.formatMessage(formMessages[CheckboxIDs.TERMS].label),
                        }}
                      />
                    }
                  />
                </Box>
              )}
            </form>
          </CardContent>
        </Card>
      </Box>

      <FormFooterArea isValid={formIsValid} submitForm={submitForm} submitButton={submitButton} />
    </ActivityOverlay>
  );
};
